import {
    useInfiniteQuery,
    UseInfiniteQueryResult,
    useMutation,
    UseMutationResult,
    useQuery,
    useQueryClient,
    UseQueryResult,
} from '@tanstack/react-query';
import {RcFile} from 'antd/lib/upload';

import {useLocale} from '../../provider/locale/locale-hook';
import {useSnackbar} from '../../provider/snackbar/snackbar-hook';
import {ApiError} from '../../util/error';
import {PublicationListFilterType} from '../publication/publication-filter-type';

import {
    createEvent,
    deleteEvent,
    fetchEventById,
    fetchEvents,
    fetchEventSelectedCompanies,
    updateEvent,
    uploadEventImage,
} from './events-api';
import {
    EventDetailType,
    EventRequestType,
    EventSelectedCompaniesType,
    EventsListResponseType,
    UploadEventImageResponseType,
} from './events-type';

const EVENTS_QUERY_KEY = 'events';
const SINGLE_EVENT_QUERY_KEY = 'single-event';

export function useEvents(
    filter?: PublicationListFilterType,
    options?: {enabled?: boolean; staleTime?: number}
): UseInfiniteQueryResult<EventsListResponseType> {
    return useInfiniteQuery([EVENTS_QUERY_KEY, filter], async ({pageParam: page = 1}) => fetchEvents(page, filter), {
        getPreviousPageParam: (firstPage) => (firstPage.previous && firstPage.page ? firstPage.page - 1 : 1),
        getNextPageParam: (lastPage) => (lastPage.next && lastPage.page ? lastPage.page + 1 : null),
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        refetchInterval: 0,
        retry: 0,
        enabled: options?.enabled ?? true,
        // Keep the cached list across tab switches; only refetch when the filter (query key) changes.
        staleTime: options?.staleTime,
    });
}

export function useEventSelectedCompanies(
    eventId: number,
    enabled: boolean
): UseInfiniteQueryResult<EventSelectedCompaniesType> {
    return useInfiniteQuery(
        ['event-selected-companies', eventId],
        async ({pageParam: page = 1}) => fetchEventSelectedCompanies(eventId, page),
        {
            getPreviousPageParam: (firstPage) => (firstPage.previous && firstPage.page ? firstPage.page - 1 : 1),
            getNextPageParam: (lastPage) => (lastPage.next && lastPage.page ? lastPage.page + 1 : null),
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
            refetchInterval: 0,
            retry: 0,
            enabled,
        }
    );
}

export function useEvent(eventId: number | null, enabled: boolean): UseQueryResult<EventDetailType> {
    return useQuery([SINGLE_EVENT_QUERY_KEY, eventId], () => fetchEventById(eventId || 0), {
        enabled: enabled && Boolean(eventId),
        retry: 0,
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        cacheTime: 0,
    });
}

function getApiErrorMessage(error: ApiError): string | undefined {
    const errorData = error?.jsonData as {non_field_errors?: Array<string>};

    return errorData?.non_field_errors?.[0];
}

export function useCreateEvent(): UseMutationResult<void, unknown, EventRequestType> {
    const {snackbar} = useSnackbar();
    const {getLocalizedString} = useLocale();
    const queryClient = useQueryClient();

    return useMutation([`create-${EVENTS_QUERY_KEY}`], (data: EventRequestType) => createEvent(data), {
        onSuccess: () => {
            snackbar.success(getLocalizedString('EVENTS__SNACKBAR__CREATE_SUCCESS'));
            queryClient.invalidateQueries([EVENTS_QUERY_KEY]);
        },
        onError: (error: ApiError) => {
            snackbar.error({
                message: getLocalizedString('EVENTS__SNACKBAR__ERROR'),
                description: getApiErrorMessage(error) ?? getLocalizedString('ERROR__SOMETHING_WENT_WRONG_DETAILS'),
            });
        },
    });
}

export function useUpdateEvent(eventId: number): UseMutationResult<void, unknown, EventRequestType> {
    const {snackbar} = useSnackbar();
    const {getLocalizedString} = useLocale();
    const queryClient = useQueryClient();

    return useMutation(
        [`update-${EVENTS_QUERY_KEY}`, eventId],
        (data: EventRequestType) => updateEvent(eventId, data),
        {
            onSuccess: () => {
                snackbar.success(getLocalizedString('EVENTS__SNACKBAR__UPDATE_SUCCESS'));
                queryClient.invalidateQueries([EVENTS_QUERY_KEY]);
                queryClient.invalidateQueries([SINGLE_EVENT_QUERY_KEY, eventId]);
            },
            onError: (error: ApiError) => {
                snackbar.error({
                    message: getLocalizedString('EVENTS__SNACKBAR__ERROR'),
                    description: getApiErrorMessage(error) ?? getLocalizedString('ERROR__SOMETHING_WENT_WRONG_DETAILS'),
                });
            },
        }
    );
}

export function useDeleteEvent(): UseMutationResult<void, unknown, number> {
    const {snackbar} = useSnackbar();
    const {getLocalizedString} = useLocale();
    const queryClient = useQueryClient();

    return useMutation([`delete-${EVENTS_QUERY_KEY}`], (eventId: number) => deleteEvent(eventId), {
        onSuccess: () => {
            snackbar.success(getLocalizedString('EVENTS__SNACKBAR__DELETE_SUCCESS'));
            queryClient.invalidateQueries([EVENTS_QUERY_KEY]);
        },
        onError: () => {
            snackbar.error({
                message: getLocalizedString('EVENTS__SNACKBAR__ERROR'),
                description: getLocalizedString('ERROR__SOMETHING_WENT_WRONG_DETAILS'),
            });
        },
    });
}

export function useUploadEventImage(): UseMutationResult<UploadEventImageResponseType, unknown, RcFile> {
    return useMutation([`upload-${EVENTS_QUERY_KEY}-image`], (file: RcFile) => uploadEventImage(file));
}
